/**
 * Chat ID Validation Utility
 *
 * Provides security-critical validation for chat IDs to prevent XSS, SQL injection,
 * and other security vulnerabilities via URL parameters.
 *
 * @module chatIdValidation
 * @security CRITICAL - All chat IDs must be validated before use
 */

/**
 * Chat ID regex patterns for validation
 *
 * Supports three formats:
 * 1. NanoID/Short ID: 21-30 alphanumeric characters (a-z, A-Z, 0-9, -, _)
 *    Example: mjvidnheulkeonmrzmprvagjspn
 *
 * 2. Compound ID (LEGACY): tenant:uuid:shortId
 *    Example: investindustrial.com:15c09810-c10b-4637-90e2-6b72cfabfecc:lalbwmcllbiplpbtzacsnwauchdngm
 *    Note: This format is supported for backward compatibility and will be deprecated in the future.
 *
 * 3. Legacy Workflow Name Format (⚠️ TEMPORARY - Backend Data Quality Issue):
 *    Example: Chat Agent2025-10-31-17-20-46-818430
 *    Pattern: Text + timestamp with dashes
 *    Note: This is a backend data quality issue. These IDs should not exist but are present
 *          in historical data. Format will be removed once backend data is cleaned up.
 *
 * @security These regex patterns validate all three formats while preventing XSS/injection
 */
const NANOID_REGEX = /^[a-zA-Z0-9_-]{21,30}$/;
const COMPOUND_ID_REGEX = /^[a-zA-Z0-9.-]+:[0-9a-f-]{36}:[a-zA-Z0-9_-]{21,30}$/;
// Temporary: Matches "Chat Agent2025-10-31-17-20-46-818430" pattern
// This is a data quality issue - workflow names incorrectly used as chat IDs
const LEGACY_WORKFLOW_NAME_REGEX = /^[A-Za-z\s]+\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2}-\d{6}$/;

/**
 * Combined chat ID validation - accepts NanoID, compound, or legacy workflow name format
 */
function isValidChatIdFormat(id: string): boolean {
  return NANOID_REGEX.test(id) || COMPOUND_ID_REGEX.test(id) || LEGACY_WORKFLOW_NAME_REGEX.test(id);
}

/**
 * Validates if a string is a valid chat ID (NanoID, compound, or legacy workflow name format)
 *
 * This function provides defense-in-depth security by validating chat IDs
 * on the frontend before making API calls. The backend also validates
 * authorization, but this prevents XSS and other injection attacks.
 *
 * @param id - The chat ID to validate
 * @returns true if valid NanoID, compound, or legacy workflow name format, false otherwise
 *
 * @example
 * ```typescript
 * isValidChatId("mjvidnheulkeonmrzmprvagjspn") // true (NanoID)
 * isValidChatId("investindustrial.com:15c09810-c10b-4637-90e2-6b72cfabfecc:lalbwmcllbiplpbtzacsnwauchdngm") // true (Compound - LEGACY)
 * isValidChatId("Chat Agent2025-10-31-17-20-46-818430") // true (Legacy Workflow Name - TEMPORARY)
 * isValidChatId("invalid-id") // false
 * isValidChatId("<script>alert('xss')</script>") // false
 * isValidChatId(null) // false
 * ```
 *
 * @security
 * - Prevents XSS attacks via malformed chat IDs
 * - Prevents SQL injection attempts
 * - Validates against known safe formats (NanoID, compound, or legacy workflow name)
 * - Trims whitespace before validation
 */
export function isValidChatId(id: string | null | undefined): id is string {
  // Type guard: check for null, undefined, or non-string types
  if (!id || typeof id !== "string") {
    return false;
  }

  // Trim whitespace and validate against allowed formats
  return isValidChatIdFormat(id.trim());
}

/**
 * Safely extracts and validates a chat ID from URL parameters
 *
 * This is a convenience wrapper around isValidChatId that also handles
 * trimming whitespace and returns null for invalid inputs.
 *
 * @param id - Raw chat ID from URL (may contain whitespace or be invalid)
 * @returns Validated and trimmed chat ID, or null if invalid
 *
 * @example
 * ```typescript
 * validateChatIdFromURL("  mjvidnheulkeonmrzmprvagjspn  ")
 * // Returns: "mjvidnheulkeonmrzmprvagjspn" (NanoID)
 *
 * validateChatIdFromURL("investindustrial.com:15c09810-c10b-4637-90e2-6b72cfabfecc:lalbwmcllbiplpbtzacsnwauchdngm")
 * // Returns: "investindustrial.com:15c09810-c10b-4637-90e2-6b72cfabfecc:lalbwmcllbiplpbtzacsnwauchdngm" (Compound - LEGACY)
 *
 * validateChatIdFromURL("invalid-id")
 * // Returns: null
 *
 * validateChatIdFromURL(null)
 * // Returns: null
 * ```
 *
 * @security
 * - Returns null for any invalid input (fail-safe behavior)
 * - Prevents passing malformed IDs to API calls
 */
export function validateChatIdFromURL(id: string | null | undefined): string | null {
  return isValidChatId(id) ? id.trim() : null;
}
